import { Link } from 'react-router';
import { useCountFromZero, useInView } from '../../lib/motion';

interface ProfileMeterProps {
  percent: number;
}

const HELPS = [
  'Your filmmaking / content creation experience',
  'Your experience with AI tools',
  'The kind of projects you create',
  'Your preferred AI models and workflows',
  'How you plan to use Frameon'
];

const R = 54;
const C = 2 * Math.PI * R;

/** Profile strength as a dial that fills when it scrolls in, beside what fills it further. */
export default function ProfileMeter({ percent }: ProfileMeterProps) {
  const [ref, seen] = useInView<HTMLDivElement>(0.35);
  const shown = useCountFromZero(percent, seen, 1300);
  const complete = percent >= 100;

  return (
    <div ref={ref} className={`meter${seen ? ' in' : ''}`}>
      <div className="meter-dial">
        <svg viewBox="0 0 140 140" role="img" aria-label={`Profile ${percent}% complete`}>
          <circle cx="70" cy="70" r={R} className="meter-track" />
          <circle cx="70" cy="70" r={R + 9} className="meter-ticks" />
          <circle
            cx="70"
            cy="70"
            r={R}
            className="meter-fill"
            strokeDasharray={C}
            strokeDashoffset={C * (1 - shown / 100)}
            transform="rotate(-90 70 70)"
          />
        </svg>
        <div className="meter-num">
          <b>{shown}%</b>
          <span className="mono">Profile</span>
        </div>
      </div>

      <div className="meter-body">
        <h2 className="d meter-title">{complete ? 'Your profile is complete.' : 'Want to improve your Early Access profile?'}</h2>
        <p className="sub">
          {complete
            ? 'Every question answered — the review has everything it needs. You can still sharpen any answer.'
            : 'Complete your creator profile to help us understand:'}
        </p>
        <ul className="helps">
          {HELPS.map((help, index) => (
            <li key={help} style={{ '--d': `${200 + index * 110}ms` } as React.CSSProperties}>
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <path d="M3 8.5 L6.5 12 L13 4.5" />
              </svg>
              {help}
            </li>
          ))}
        </ul>
        <div className="actions">
          <Link className="btn" to="/profile">
            {complete ? 'Review my profile →' : 'Complete my profile →'}
          </Link>
        </div>
        <p className="mono meter-note">The more we understand your workflow, the better we can evaluate your application.</p>
      </div>
    </div>
  );
}
